import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  ViewEncapsulation,
} from '@angular/core';

export type RvButtonVariant =
  | 'primary'
  | 'dark'
  | 'secondary'
  | 'tertiary'
  | 'green-text';
export type RvButtonSize = 'large' | 'small';
export type RvButtonShape = 'squared' | 'round';

@Component({
  selector: 'button[rv-button], a[rv-button]',
  exportAs: 'rvButton',
  template: `
    <span class="rv-button__content"><ng-content /></span>
    @if (loading()) {
      <span class="rv-button__spinner" aria-hidden="true"></span>
    }
  `,
  styleUrl: './button.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'rv-button',
    '[class]': 'modifierClasses()',
    '[attr.disabled]': 'isNativeButton && disabled() ? "" : null',
    '[attr.aria-disabled]':
      'isInert() && !(isNativeButton && disabled()) ? "true" : null',
    '[attr.aria-busy]': 'loading() ? "true" : null',
    '[attr.tabindex]': '!isNativeButton && disabled() ? "-1" : null',
  },
})
export class RvButtonComponent {
  variant = input<RvButtonVariant>('primary');
  size = input<RvButtonSize>('large');
  shape = input<RvButtonShape>('squared');
  /** Visual selected state. Pair with `aria-pressed` or `aria-current` to expose it. */
  selected = input(false, { transform: booleanAttribute });
  /** Replaces the content with a spinner and blocks activation, keeping focus and the accessible name. */
  loading = input(false, { transform: booleanAttribute });
  disabled = input(false, { transform: booleanAttribute });

  private readonly element =
    inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  protected readonly isNativeButton = this.element.tagName === 'BUTTON';

  protected isInert = computed(() => this.disabled() || this.loading());

  protected modifierClasses = computed(() =>
    [
      `rv-button--${this.variant()}`,
      `rv-button--${this.size()}`,
      `rv-button--${this.shape()}`,
      this.selected() ? 'rv-button--selected' : '',
      this.loading() ? 'rv-button--loading' : '',
      this.disabled() ? 'rv-button--disabled' : '',
    ]
      .filter(Boolean)
      .join(' '),
  );

  constructor() {
    // Capture phase so the block runs before (click) handlers bound on the same host.
    const blockWhenInert = (event: Event) => {
      if (this.isInert()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    this.element.addEventListener('click', blockWhenInert, true);
    inject(DestroyRef).onDestroy(() =>
      this.element.removeEventListener('click', blockWhenInert, true),
    );
  }
}
